/**
 * Custom hook for using WebSocket service in React components
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    websocketService,
    WebSocketMessage,
    MessageType,
    FunctionCallRequest
} from 'services/websocketService';

interface UseWebSocketOptions {
    apiHost: string;
    token?: string;
    autoConnect?: boolean;
    debugMode?: boolean;
    onFunctionCallRequest?: (request: FunctionCallRequest) => Promise<any>;
    onDebugMessage?: (direction: 'sent' | 'received', message: WebSocketMessage) => void;
}

export function useWebSocket({
    apiHost,
    token,
    autoConnect = true,
    debugMode = false,
    onFunctionCallRequest,
    onDebugMessage
}: UseWebSocketOptions) {
    const isConnecting = useRef(false);
    const [isConnected, setIsConnected] = useState(false);
    const [searchParams] = useSearchParams();

    // Track connection status changes
    useEffect(() => {
        const handleConnect = () => setIsConnected(true);
        const handleDisconnect = () => setIsConnected(false);

        const unsubscribeConnect = websocketService.onConnect(handleConnect);
        const unsubscribeDisconnect = websocketService.onDisconnect(handleDisconnect);

        // Set initial state
        setIsConnected(websocketService.isConnected());

        return () => {
            unsubscribeConnect();
            unsubscribeDisconnect();
        };
    }, []);

    // Connect to WebSocket
    useEffect(() => {
        if (autoConnect && !isConnecting.current && apiHost) {
            isConnecting.current = true;
            websocketService.connect(apiHost, token, debugMode);

            return () => {
                websocketService.disconnect();
                isConnecting.current = false;
            };
        }
    }, [apiHost, token, autoConnect, debugMode]);

    // Handle function call requests from server
    useEffect(() => {
        if (!onFunctionCallRequest) return;

        const unsubscribe = websocketService.on('function_call_request', async (message: WebSocketMessage) => {
            console.log('[useWebSocket] Received function_call_request message:', message);
            const request = message.payload as FunctionCallRequest;
            console.log('[useWebSocket] Parsed request:', request);

            try {
                if (searchParams.get("debug") === "true") {
                    const min = 1;
                    const max = 100;
                    const rand = min + Math.random() * (max - min);

                    websocketService.sendFunctionCallResponse({
                        requestId: request.requestId,
                        result: {
                            reponse: rand
                        }, // Placeholder result for testing          
                    });
                }
                else {
                    await onFunctionCallRequest(request);
                    console.log('[useWebSocket] Function call was executed successfully');
                }
            } catch (error: any) {
                websocketService.sendFunctionCallResponse({
                    requestId: request.requestId,
                    result: null,
                    error: error.message || 'Function call failed'
                });
            }
        });

        return unsubscribe;
    }, [onFunctionCallRequest, searchParams]);

    // Handle debug messages
    useEffect(() => {
        if (!onDebugMessage || !debugMode) return;

        const unsubscribe = websocketService.onDebug((direction, message) => {
            onDebugMessage(direction, message);
        });

        return unsubscribe;
    }, [onDebugMessage, debugMode]);

    // Send message
    const sendMessage = useCallback((message: WebSocketMessage) => {
        websocketService.send(message);
    }, []);

    // Subscribe to message type
    const subscribe = useCallback((messageType: MessageType, handler: (message: WebSocketMessage) => void) => {
        return websocketService.on(messageType, handler);
    }, []);

    // Call a function on the server
    const callFunction = useCallback(async (
        functionName: string,
        parameters: Record<string, any>,
        timeoutMs?: number
    ) => {
        return websocketService.callFunction(functionName, parameters, timeoutMs);
    }, []);

    return {
        sendMessage,
        subscribe,
        isConnected,
        callFunction,
        websocketService
    };
}
